import { Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ClientPaginatedTable } from 'components/table';
import * as TableConfig from './TablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useEffect, useState } from 'react';
import { IFeatureFlag } from 'types/system-settings.types';
import { updateFeatureFlag } from 'services/feature-flags.service';
import { DataType } from 'components/table/types';
import { systemSettingsNamesMapping } from 'constants/system-settings.constants';

type IProps = {
	data: IFeatureFlag[];
	prefix: string;
	editPermissions: boolean;
	requestChangeConfirmation: (settingName: string, onConfirm: () => void, onCancel: () => void) => void;
};

// Define table structure for feature flags
const featureFlagsTableColumns: Record<string, DataType> = {
	name: 'custom',
	description: 'custom',
	isEnabled: 'custom',
};

// Define which columns are editable and which ones show edit icons
const editableColumns = ['description', 'isEnabled'];
const editIconColumns = ['isEnabled'];

export const FeatureFlags: React.FC<IProps> = ({
	data,
	prefix,
	editPermissions,
	requestChangeConfirmation,
}: IProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const [tableData, setTableData] = useState<Record<string, any>[]>([]);

	useEffect(() => {
		setDefaultModifiedItems();
	}, [data]);

	const setDefaultModifiedItems = () => {
		const modifiedItems = data?.map((item: any) => {
			return { ...item, editable: false };
		});
		setTableData(modifiedItems || []);
	};

	const handleEditButtonClick = (itemId: number) => {
		const modifiedItems = data?.map((item: any) => {
			return { ...item, editable: itemId === item.id ? true : false };
		});
		setTableData(modifiedItems || []);
	};

	const saveItemChanges = (newItemData: any) => {
		const { name, description, isEnabled } = newItemData;
		const mappedName = systemSettingsNamesMapping[name] || name;

		requestChangeConfirmation(
			mappedName,
			() => {
				dispatch(updateFeatureFlag(name, isEnabled, description));
			},
			() => {
				setDefaultModifiedItems();
			}
		);
	};

	const cancelItemChanges = () => {
		setDefaultModifiedItems();
	};

	const updateModifiedItem = (field: any, fieldValue: any, changedItem: any) => {
		const modifiedItems = tableData?.map((item: any) => {
			return {
				...item,
				editable: changedItem.id === item.id ? true : false,
				[field]: changedItem.id === item.id ? fieldValue : item[field],
			};
		});
		setTableData(modifiedItems || []);
	};

	if (!data || data.length === 0) {
		return (
			<Stack spacing={2}>
				<Typography variant="subtitle" color={theme.palette.root.grey}>
					{t(`${prefix}.no_flags_message`)}
				</Typography>
			</Stack>
		);
	}

	return (
		<Stack spacing={2}>
			<Stack flexDirection={'row'} justifyContent={'space-between'}>
				<Typography variant="subtitle" color={theme.palette.root.grey}>
					{data.length + ' ' + t('messages.results')}
				</Typography>
			</Stack>
			{tableData && (
				<ClientPaginatedTable
					data={TableConfig.tableData(tableData)}
					columns={TableConfig.buildColumns(
						t,
						handleEditButtonClick,
						saveItemChanges,
						cancelItemChanges,
						updateModifiedItem,
						editPermissions,
						theme,
						editableColumns,
						editIconColumns,
						featureFlagsTableColumns,
						`${prefix}.table.column`
					)}
					dateFormat="dd.MM.yyyy"
				/>
			)}
		</Stack>
	);
};
